import { z } from "zod";
import { ROLES } from "../enums.js";
import { phoneSchema } from "../phone-schema.js";
import { isoDateSchema, isoTimestampSchema } from "./common.js";

export const magicLinkRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(200),
});
export type MagicLinkRequest = z.infer<typeof magicLinkRequestSchema>;

export const verifyRequestSchema = z.object({ token: z.string().min(10).max(200) });
export type VerifyRequest = z.infer<typeof verifyRequestSchema>;

// Extension 3: login by phone number and a 6-digit SMS code.
export const SMS_CODE_LENGTH = 6;

export const smsCodeRequestSchema = z.object({ phone: phoneSchema });
export type SmsCodeRequest = z.infer<typeof smsCodeRequestSchema>;

export const smsVerifyRequestSchema = z.object({
  phone: phoneSchema,
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/),
});
export type SmsVerifyRequest = z.infer<typeof smsVerifyRequestSchema>;

export const loginRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(200),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const assignmentSummarySchema = z.object({
  id: z.string(),
  zvEmployeeId: z.number().int(),
  zvOrderId: z.number().int(),
  zvPositionId: z.number().int(),
  zvAssignmentId: z.number().int(),
  zvCustomerId: z.number().int(),
  customerName: z.string(),
  positionLabel: z.string(),
  dateFrom: isoDateSchema,
  dateTo: isoDateSchema,
  hours: z.object({
    mon: z.number(),
    tue: z.number(),
    wed: z.number(),
    thu: z.number(),
    fri: z.number(),
    sat: z.number(),
    sun: z.number(),
  }),
  weeklyHours: z.number(),
  endedAt: isoTimestampSchema.nullable(),
});
export type AssignmentSummary = z.infer<typeof assignmentSummarySchema>;

export const employeeSummarySchema = z.object({
  zvEmployeeId: z.number().int(),
  firstName: z.string(),
  lastName: z.string(),
  personnelNo: z.number().int().nullable(),
  isActive: z.boolean(),
  entryDate: isoDateSchema.nullable(),
  exitDate: isoDateSchema.nullable(),
});
export type EmployeeSummary = z.infer<typeof employeeSummarySchema>;

export const meResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    role: z.enum(ROLES),
    // Employees may have an e-mail, a phone number (E.164) or both (extension 3).
    email: z.string().nullable(),
    phone: z.string().nullable(),
    name: z.string(),
    zvEmployeeId: z.number().int().nullable(),
    hasPassword: z.boolean(),
  }),
  employee: employeeSummarySchema.nullable(),
  activeAssignments: z.array(assignmentSummarySchema),
  unreadNotifications: z.number().int(),
  zvooveMode: z.enum(["mock", "real"]),
  // Feature switches the UI follows (extension 2: sick reports, `settings.sick_reports_enabled`).
  features: z.object({ sickReports: z.boolean() }),
});
export type MeResponse = z.infer<typeof meResponseSchema>;
